/*
 * flyweight-chat-open.js
 * Added 10 Aug 2026.
 *
 * Opens the Flyweight chat in place instead of reloading the page.
 *
 * THE PROBLEM
 * Chat links across the site are written into CONTENT (collection descriptions,
 * page bodies) as plain anchors with a query-string href:
 *
 *     <a href="?flyweight-chat=Which+parts+are+compatible+with+my+bike%3F">Ask TBA Guide</a>
 *
 * An anchor with an href navigates. The browser leaves the current page and
 * loads the same path with a new query string, which is a full page load. The
 * chat may well end up open, but the visitor has lost their scroll position and
 * paid for a round trip.
 *
 * THE FIX
 * One delegated listener on `document`. Because every template - collection,
 * blog article, page, product, index - renders into the same document, a single
 * listener here covers links added anywhere, including ones pasted into content
 * later. Nothing needs to be added to the links themselves.
 *
 * The listener cancels the navigation and dispatches Flyweight's own event:
 *
 *     window.dispatchEvent(new Event('flyweightchatopen'));
 *     window.dispatchEvent(new CustomEvent('flyweightchatopen', {
 *       detail: { message: 'Where is my order?' }
 *     }));
 *
 * NOTE: `detail.message` does not pre-fill the composer - it SENDS the message
 * immediately and the assistant answers. Only use it for questions that should
 * be asked automatically.
 *
 * WHAT IT MATCHES
 *   a[href*="flyweight-chat"]   the existing content links, in all three shapes
 *                               currently in use: "?flyweight-chat=<question>",
 *                               "?flyweight-chat" and "/?flyweight-chat"
 *   [data-flyweight-open]       optional hook for future links, with an
 *                               optional data-flyweight-message attribute
 *
 * The question is read from the href's own query string, so existing links keep
 * working with no edit to any collection description or page body.
 *
 * DELIBERATELY NOT HANDLED
 * Modifier-clicks and middle-clicks are left alone, so cmd/ctrl-click still
 * opens the href in a new tab rather than being swallowed.
 *
 * If the widget has not booted yet the event would land on nothing, so the
 * dispatch waits for window.FlyweightChat to appear (up to ~8s) and then fires.
 */
(function () {
  'use strict';

  var SELECTOR = 'a[href*="flyweight-chat"], [data-flyweight-open]';
  var PARAM = 'flyweight-chat';
  var POLL_MS = 200;
  var POLL_LIMIT = 40; // ~8 seconds

  function buildEvent(message) {
    if (message) {
      return new CustomEvent('flyweightchatopen', { detail: { message: message } });
    }
    return new Event('flyweightchatopen');
  }

  function openChat(message) {
    if (window.FlyweightChat) {
      window.dispatchEvent(buildEvent(message));
      return;
    }

    // Widget script hasn't finished loading. Wait for it rather than dropping
    // the click on the floor.
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (window.FlyweightChat) {
        clearInterval(timer);
        window.dispatchEvent(buildEvent(message));
      } else if (tries >= POLL_LIMIT) {
        clearInterval(timer);
      }
    }, POLL_MS);
  }

  function messageFor(el) {
    var href = el.getAttribute('href') || '';
    var q = href.indexOf('?');

    if (q !== -1) {
      try {
        // URLSearchParams decodes %XX and treats "+" as a space, which is how
        // these hrefs are encoded.
        var value = new URLSearchParams(href.slice(q + 1)).get(PARAM);
        if (value) return value;
      } catch (e) {
        // fall through to the data attribute
      }
    }

    return el.getAttribute('data-flyweight-message') || '';
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;

    // Let the browser handle new-tab / new-window / download intents.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var trigger = target.closest(SELECTOR);
    if (!trigger) return;

    event.preventDefault();
    openChat(messageFor(trigger));
  });
})();
